import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Save, TestTube2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * IPDStickerPrint — repeat the current IPD patient's core identifiers across an
 * A4 sticker sheet (3-column reference). All settings are stored in
 * localStorage under `ipd_sticker_settings` so each hospital's roll matches
 * the physical peel-off sheet they buy.
 */

const DEFAULTS = {
  columns: 3,
  sticker_width_mm: 66,
  sticker_height_mm: 28,
  h_gap_mm: 3,
  v_gap_mm: 2,
  top_margin_mm: 8,
  left_margin_mm: 5,
  font_pt: 9,
  count: 30,
};
const LS_KEY = "ipd_sticker_settings";

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch { return { ...DEFAULTS }; }
};

export default function IPDStickerPrint() {
  const { id } = useParams();
  const [ipd, setIpd] = useState(null);
  const [patient, setPatient] = useState(null);
  const [cfg, setCfg] = useState(loadSettings());

  const load = useCallback(async () => {
    try {
      const { data: ipdDoc } = await api.get(`/ipd/${id}`);
      setIpd(ipdDoc);
      if (ipdDoc.patient_id) {
        const { data: p } = await api.get(`/patients/${ipdDoc.patient_id}`);
        setPatient(p);
      }
    } catch { toast.error("Failed to load IPD"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setNum = (k, min, max) => (e) => {
    const raw = e.target.value;
    if (raw === "") return set(k, "");
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    set(k, Math.max(min, Math.min(max, n)));
  };
  const save = () => {
    // Coerce any accidentally-empty numeric fields back to defaults so we
    // never persist "" and break the grid `repeat(, …)` template on next
    // load.
    const clean = {};
    for (const [k, v] of Object.entries(cfg)) {
      clean[k] = v === "" || v == null ? DEFAULTS[k] : v;
    }
    setCfg(clean);
    localStorage.setItem(LS_KEY, JSON.stringify(clean));
    toast.success("Sticker layout saved");
  };
  const reset = () => { setCfg({ ...DEFAULTS }); localStorage.removeItem(LS_KEY); toast("Reverted to defaults"); };
  const doPrint = () => window.print();
  const testPrint = () => {
    // Test print = just 3 stickers so ops can waste less paper aligning.
    const prev = cfg.count;
    setCfg({ ...cfg, count: 3 });
    setTimeout(() => { window.print(); setCfg({ ...cfg, count: prev }); }, 100);
  };

  const pmjayLabel = useMemo(() => {
    const t = (ipd?.tpa_insurer || patient?.pmjay_tpa || "").toString().toLowerCase();
    const isPmjay = !!patient?.pmjay || t.includes("pmjay") || t.includes("pm jay") || t.includes("pmjy");
    return isPmjay ? "PM Jay" : "Non PM Jay";
  }, [ipd, patient]);

  const dataLines = useMemo(() => {
    if (!ipd || !patient) return null;
    return {
      uhid: patient.uhid || "",
      ipd_no: ipd.ipd_no || "",
      pmjay: pmjayLabel,
      name: patient.name || "",
      age_gender: `${patient.age ?? "-"}/${patient.gender || "-"}`,
      mobile: patient.mobile || "",
    };
  }, [ipd, patient, pmjayLabel]);

  // Sheet layout — the sheet uses precise mm units so the physical peel-off
  // labels line up perfectly. `pageBreakInside` on each sticker keeps a card
  // from splitting across pages.
  const sheetStyle = {
    padding: `${cfg.top_margin_mm}mm 0 0 ${cfg.left_margin_mm}mm`,
  };
  const gridStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(${cfg.columns}, ${cfg.sticker_width_mm}mm)`,
    columnGap: `${cfg.h_gap_mm}mm`,
    rowGap: `${cfg.v_gap_mm}mm`,
  };
  const stickerStyle = {
    width: `${cfg.sticker_width_mm}mm`,
    height: `${cfg.sticker_height_mm}mm`,
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: `${cfg.font_pt}pt`,
    lineHeight: 1.25,
    padding: "2mm 3mm",
    boxSizing: "border-box",
    color: "#111",
    overflow: "hidden",
    pageBreakInside: "avoid",
    breakInside: "avoid",
    border: "1px dashed #cbd5e1",
  };

  const N = Math.max(1, Math.min(500, Number(cfg.count) || 1));
  const stickers = useMemo(() => Array.from({ length: N }), [N]);

  if (!ipd || !patient || !dataLines) {
    return <div className="p-10 text-center text-slate-500">Loading IPD sticker data…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Config bar — hidden on print */}
      <div className="no-print bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-display text-lg font-semibold">IPD Stickers — {ipd.ipd_no}</h1>
              <p className="text-[11px] text-slate-500">
                Patient: <b>{patient.name}</b> · UHID <span className="font-mono">{patient.uhid}</span>
                {" · "}A4 · 3-column reference (configurable)
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={testPrint} className="rounded-sm" data-testid="sticker-test-print">
                <TestTube2 className="w-4 h-4 mr-1"/> Test Print (3)
              </Button>
              <Button variant="outline" onClick={save} className="rounded-sm" data-testid="sticker-save">
                <Save className="w-4 h-4 mr-1"/> Save Layout
              </Button>
              <Button variant="outline" onClick={reset} className="rounded-sm" data-testid="sticker-reset">
                <RotateCcw className="w-4 h-4 mr-1"/> Reset
              </Button>
              <Button onClick={doPrint} className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid="sticker-print">
                <Printer className="w-4 h-4 mr-1"/> Print ({N})
              </Button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 md:grid-cols-9 gap-2 text-[11px]">
            <Field label="Count" value={cfg.count} onChange={setNum("count", 1, 500)} testid="sticker-cfg-count"/>
            <Field label="Columns" value={cfg.columns} onChange={setNum("columns", 1, 6)} testid="sticker-cfg-columns"/>
            <Field label="Width (mm)" value={cfg.sticker_width_mm} onChange={setNum("sticker_width_mm", 20, 120)}/>
            <Field label="Height (mm)" value={cfg.sticker_height_mm} onChange={setNum("sticker_height_mm", 12, 80)}/>
            <Field label="H Gap" value={cfg.h_gap_mm} onChange={setNum("h_gap_mm", 0, 20)}/>
            <Field label="V Gap" value={cfg.v_gap_mm} onChange={setNum("v_gap_mm", 0, 20)}/>
            <Field label="Top Margin" value={cfg.top_margin_mm} onChange={setNum("top_margin_mm", 0, 40)}/>
            <Field label="Left Margin" value={cfg.left_margin_mm} onChange={setNum("left_margin_mm", 0, 40)}/>
            <Field label="Font (pt)" value={cfg.font_pt} onChange={setNum("font_pt", 6, 16)}/>
          </div>
        </div>
      </div>

      {/* Sticker sheet — the only element that prints */}
      <div className="print-area">
        <div style={sheetStyle}>
          <div style={gridStyle}>
            {stickers.map((_, i) => (
              <div key={`stk-${i}`} style={stickerStyle} data-testid={`sticker-${i}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "6mm" }}>
                  <span>UHID No. : <b>{dataLines.uhid}</b></span>
                  <span>IPD No. : <b>{dataLines.ipd_no}</b></span>
                </div>
                <div>{dataLines.pmjay === "PM Jay" ? <><b>PM Jay</b> / Non PM Jay</> : <>PM Jay / <b>Non PM Jay</b></>}</div>
                <div>Name : <span style={{ paddingLeft: "3mm" }}>{dataLines.name}</span></div>
                <div>Age/Gender : {dataLines.age_gender}</div>
                <div>Mobile No : {dataLines.mobile}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Print-only CSS — hide everything except .print-area, and reset A4 page. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .print-area { padding: 0 !important; }
        }
        @media screen {
          .print-area { padding: 24px 8px; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1">
      <span className="text-slate-500 text-[10px] uppercase tracking-wider">{label}</span>
      <Input
        type="number"
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="h-6 text-xs w-16 border-0 bg-white p-1"
      />
    </label>
  );
}
